import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { postActions, postSelectors } from 'redux/post';
import { translationSelectors } from 'redux/translation';
import PostCard from 'components/Post/PostCard';
import PostTargets from 'components/Post/PostTargets';
import PostTargetUsersCount from 'components/Post/PostTargetUsersCount';
import PostActivity from 'components/Post/PostActivity';
import PropTypes from 'prop-types';
import history from 'providers/history';
import PostAttachments from 'components/Post/PostAttachments';
import PostPreHeader from 'components/Post/PostPreHeader';
import { isMobile } from 'helpers';
import PostHeaderDraft from 'components/Post/PostHeaderDraft';
import PostHeaderScheduled from 'components/Post/PostHeaderScheduled';
import PostQuiz from './PostQuiz';
import PostPoll from './PostPoll';
import PostFooter from './PostFooter';
import PostInfo from './PostInfo';
import PostOpengraph from './PostOpengraph';

const Post = ({ isCompact = false, postId, feedName, ...rest }) => {

  const dispatch = useDispatch();

  const [translated, _setTranslated] = useState(false);
  const [pollDataRevealed, setPollDataRevealed] = useState(false);

  const fetchTranslation = () => dispatch(postActions.getTranslations({ postId }));

  const setTranslated = value => {
    _setTranslated(value);
    _setTranslated(current => {
      if (current) {
        fetchTranslation();
      }

      return current;
    });
  };

  const post = useSelector(state => postSelectors.getPost(state, postId));

  const getTranslation = useSelector(translationSelectors.getTranslationSelector);

  const { body, bodyHtml } = useMemo(() => {
    if (!post || post.deleted) {
      return {};
    }

    if (!translated) {
      return { body: post.body, bodyHtml: post.bodyHtml };
    }

    return {
      body: getTranslation('post', postId, 'body', post.body),
      bodyHtml: getTranslation('post', postId, 'body_html', null),
    };
  }, [getTranslation, post, postId, translated]);

  if (!post || post.deleted) {
    return null;
  }

  const { user, media, poll, quiz, attachments, activity, targets, targetUsersCount, opengraph, isDraft, isFollowing, scheduledAt, postedAt } = post;

  const headerProps = {
    memberId: user.memberId,
    firstName: user.firstName,
    lastName: user.lastName,
    avatar: user.avatar,
    info: <PostInfo postId={postId} />,
  };

  const linkToPost = isCompact ? {
    onClick: e => e.target?.tagName !== 'A' ? history.push(`/post/${postId}`) : null,
  } : {};

  const isImportant = post.important;
  const isPinned = !!post.pinned;
  const isPinnable = !!post?.feedPinnable?.[feedName]; // Assume without feedname it's single post view

  const hasMenu = [
    post.permissions.pin && isPinnable,
    post.permissions.important,
    post.permissions.follow,
    post.permissions.poll && post.permissions.poll.viewVoters && !isMobile,
    post.permissions.poll && post.permissions.poll.downloadVotes,
    post.permissions.update,
    post.permissions.delete,
    post.permissions.shareToNetwork,
    post.isDraft,
  ].reduce((acc, v) => acc || v);

  const context = { isCompact, isImportant, isPinned, isPinnable, isFollowing, isDraft, hasMenu, translated, setTranslated, pollDataRevealed, setPollDataRevealed };

  let preheader = null;

  if (isDraft) {
    preheader = <PostHeaderDraft />;
  } else if (!postedAt && scheduledAt) {
    preheader = <PostHeaderScheduled scheduledAt={scheduledAt} />;
  } else if (targets && targets.length > 0) {
    preheader = <PostTargets targets={targets} />;
  } else if (targetUsersCount && targetUsersCount > 0) {
    preheader = <PostTargetUsersCount count={targetUsersCount} />;
  }

  return (
    <PostCard
      postId={postId}
      context={context}
      preheader={(preheader || (isPinnable && isPinned)) ? <PostPreHeader>{preheader}</PostPreHeader> : null}
      header={post.show?.header ? headerProps : null}
      beforeBody={activity ? <PostActivity activity={activity} {...linkToPost} /> : null}
      body={body}
      bodyHtml={bodyHtml}
      bodyProps={linkToPost}
      media={media}
      extras={(
        <>
          {poll && <PostPoll pollId={poll} />}
          {quiz && <PostQuiz quizId={quiz} />}
          {attachments && <PostAttachments attachments={attachments} />}
        </>
      )}
      {...rest}
    >
      {opengraph && !media?.length && <PostOpengraph {...opengraph} />}
      <PostFooter postId={postId} />
    </PostCard>
  );
};

Post.propTypes = {
  postId: PropTypes.any,
  isCompact: PropTypes.bool,
  feedName: PropTypes.string,
};

export default Post;
